import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { user as userApi, motivos as motivosApi } from '../services/api';
import { getCustomColors, setCustomColor, resetColors, hasCustomColors } from '../services/colorPreferences';
import { Moon, Sun, Monitor, User, LogOut, Calendar, Home, Plus, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Palette, RotateCcw } from 'lucide-react';

const SettingsGroup = ({ title, children, defaultOpen = false, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div
            className="rounded-2xl border overflow-hidden transition-all duration-300"
            style={{
                background: 'var(--bg-card)',
                borderColor: 'var(--border-color)'
            }}
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                type="button"
            >
                <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        {title}
                    </span>
                </div>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
            </button>

            <div
                className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                style={{ overflow: 'hidden' }}
            >
                <div className="p-5 pt-0 border-t border-white/5">
                    {children}
                </div>
            </div>
        </div>
    );
};

const Settings = () => {
    const { theme, setTheme } = useTheme();
    const { user, logout } = useAuth();
    const toast = useToast();

    // Settings state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [conductor, setConductor] = useState(null);
    const [gruposVacaciones, setGruposVacaciones] = useState([]);
    const [gruposLibranzas, setGruposLibranzas] = useState([]);
    const [selectedVacaciones, setSelectedVacaciones] = useState('');
    const [selectedLibranzas, setSelectedLibranzas] = useState('');
    const [selectedTurno, setSelectedTurno] = useState('Mañana');
    const [selectedTipo, setSelectedTipo] = useState('bei');

    // Anexo libranzas state
    const [anexoDias, setAnexoDias] = useState([]);
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear] = useState(2026);

    // Custom colors state
    const [turnos, setTurnos] = useState([]);
    const [customColors, setCustomColors] = useState({});
    const [colorLoading, setColorLoading] = useState(true);

    const themeOptions = [
        { id: 'dark', icon: Moon, label: 'Oscuro', description: 'Tema oscuro por defecto' },
        { id: 'dim', icon: Monitor, label: 'Intermedio', description: 'Balance entre oscuro y claro' },
        { id: 'light', icon: Sun, label: 'Claro', description: 'Modo claro para luz del día' }
    ];

    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    // Load settings on mount
    useEffect(() => {
        loadSettings();
        loadTurnos();
    }, []);

    // Load turnos for color customization
    const loadTurnos = async () => {
        try {
            setColorLoading(true);
            const turnosData = await motivosApi.getTurnos();
            setTurnos(turnosData || []);
            setCustomColors(getCustomColors());
        } catch (e) {
            console.warn('Could not load turnos for colors:', e);
        } finally {
            setColorLoading(false);
        }
    };

    // Handle color change
    const handleColorChange = (turnoName, color) => {
        setCustomColor(turnoName, color);
        setCustomColors(prev => ({ ...prev, [turnoName]: color }));
    };

    // Reset all colors
    const handleResetColors = () => {
        resetColors();
        setCustomColors({});
        toast.success('Colores restablecidos');
    };

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await userApi.getSettings();
            setConductor(data.conductor);
            setGruposVacaciones(data.grupos_vacaciones || []);
            setGruposLibranzas(data.grupos_libranzas || []);
            setSelectedVacaciones(data.conductor?.grupo_vacaciones_id || '');
            setSelectedLibranzas(data.conductor?.grupo_libranza_id || '');
            setSelectedTurno(data.conductor?.turno || 'Mañana');
            setSelectedTipo(data.conductor?.tipo || 'bei');

            // Load anexo libranzas
            const anexoData = await userApi.getAnexoLibranzas(2026);
            setAnexoDias(anexoData || []);
        } catch (error) {
            console.error('Error loading settings:', error);
            // Don't show error toast if user doesn't have conductor linked
            if (!error.message?.includes('perfil de conductor')) {
                toast.error('Error al cargar configuración');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSaveGroups = async () => {
        try {
            setSaving(true);
            await userApi.updateSettings({
                grupo_vacaciones_id: selectedVacaciones || null,
                grupo_libranza_id: selectedLibranzas || null,
                turno: selectedTurno,
                tipo: selectedTipo
            });
            toast.success('Grupos actualizados correctamente');
        } catch (error) {
            toast.error(error.message || 'Error al guardar grupos');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleAnexoDay = async (dateStr) => {
        const existing = anexoDias.find(d => d.fecha === dateStr);
        try {
            if (existing) {
                await userApi.deleteAnexoLibranza(existing.id);
                setAnexoDias(prev => prev.filter(d => d.id !== existing.id));
                toast.success('Día eliminado');
            } else {
                await userApi.addAnexoLibranza(dateStr);
                // Reload to get the new ID
                const updated = await userApi.getAnexoLibranzas(2026);
                setAnexoDias(updated || []);
                toast.success('Día añadido');
            }
        } catch (error) {
            toast.error(error.message || 'Error al actualizar día');
        }
    };

    // Generate calendar days for current month
    const generateCalendarDays = () => {
        const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
        const firstDay = new Date(calendarYear, calendarMonth, 1).getDay();
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;

        const days = [];
        for (let i = 0; i < startOffset; i++) {
            days.push({ empty: true, key: `empty-${i}` });
        }
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = anexoDias.some(d => d.fecha === dateStr);
            days.push({ day, dateStr, isSelected, key: dateStr });
        }
        return days;
    };

    const anexoSet = new Set(anexoDias.map(d => d.fecha));

    return (
        <div className="space-y-6 max-w-2xl mx-auto pb-20">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    Ajustes
                </h1>
                <p style={{ color: 'var(--text-secondary)' }} className="text-sm mt-1">
                    Personaliza tu experiencia
                </p>
            </div>

            {/* Theme Selection */}
            <SettingsGroup title="🎨 Tema de la aplicación" defaultOpen={true}>
                <div className="space-y-2 mt-2">
                    {themeOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = theme === option.id;
                        return (
                            <button
                                key={option.id}
                                onClick={() => setTheme(option.id)}
                                className={`
                                    w-full flex items-center gap-4 p-4 rounded-xl transition-all
                                    ${isSelected
                                        ? 'bg-cyan-500/20 border-cyan-500/50'
                                        : 'hover:bg-[var(--bg-card-hover)]'
                                    }
                                `}
                                style={{
                                    border: isSelected ? undefined : '1px solid var(--border-color)'
                                }}
                            >
                                <div className={`
                                    w-10 h-10 rounded-xl flex items-center justify-center
                                    ${isSelected ? 'bg-cyan-500/30' : 'bg-[var(--bg-tertiary)]'}
                                `}>
                                    <Icon className={`w-5 h-5 ${isSelected ? 'text-cyan-400' : ''}`}
                                        style={{ color: isSelected ? undefined : 'var(--text-secondary)' }} />
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                        {option.label}
                                    </p>
                                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                        {option.description}
                                    </p>
                                </div>
                                {isSelected && (
                                    <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </SettingsGroup>

            {/* Calendar Colors */}
            <SettingsGroup title="🎨 Colores del Calendario">
                <div className="space-y-4">
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        Personaliza los colores de cada turno en tu calendario.
                    </p>

                    {colorLoading ? (
                        <div className="text-center py-4">
                            <div className="animate-spin w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {turnos
                                    .filter(t => ['Mañana', 'Tarde', 'Noche', 'Partido', 'Vacaciones', 'Quincena', 'Libranza'].includes(t.nombre))
                                    .map(turno => (
                                        <div
                                            key={turno.id}
                                            className="flex items-center justify-between p-3 rounded-xl"
                                            style={{ background: 'var(--bg-tertiary)' }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-8 h-8 rounded-lg shadow-inner"
                                                    style={{ backgroundColor: customColors[turno.nombre] || turno.color_bg }}
                                                />
                                                <span style={{ color: 'var(--text-primary)' }} className="font-medium">
                                                    {turno.nombre}
                                                </span>
                                                {customColors[turno.nombre] && (
                                                    <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                                                        Personalizado
                                                    </span>
                                                )}
                                            </div>
                                            <input
                                                type="color"
                                                value={customColors[turno.nombre] || turno.color_bg}
                                                onChange={(e) => handleColorChange(turno.nombre, e.target.value)}
                                                className="w-10 h-10 rounded-lg cursor-pointer border-2 border-white/10 hover:border-cyan-500/50 transition-colors"
                                                style={{ background: 'transparent' }}
                                            />
                                        </div>
                                    ))
                                }
                            </div>

                            {hasCustomColors() && (
                                <button
                                    onClick={handleResetColors}
                                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-colors"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Restablecer colores originales
                                </button>
                            )}
                        </>
                    )}
                </div>
            </SettingsGroup>

            {/* Group Settings - Only show if conductor is linked */}
            {!loading && conductor && (
                <>
                    {/* Groups Configuration */}
                    <SettingsGroup title="📅 Configuración de Turnos">
                        <div className="space-y-6">
                            {/* Turno Inicial */}
                            <div>
                                <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                    Turno Inicial del Año
                                </h3>
                                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                                    Turno con el que empiezas la primera semana del año
                                </p>
                                <div className="flex gap-3">
                                    {['Mañana', 'Tarde'].map((turnoOption) => (
                                        <button
                                            key={turnoOption}
                                            onClick={() => setSelectedTurno(turnoOption)}
                                            className={`
                                                flex-1 p-4 rounded-xl font-semibold transition-all border
                                                ${selectedTurno === turnoOption
                                                    ? 'border-cyan-500 bg-cyan-500/20'
                                                    : 'border-[var(--border-color)] hover:bg-[var(--bg-card-hover)]'
                                                }
                                            `}
                                            style={{
                                                color: selectedTurno === turnoOption ? '#22d3ee' : 'var(--text-primary)'
                                            }}
                                        >
                                            {turnoOption === 'Mañana' ? '☀️ Mañana' : '🌙 Tarde'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tipo de Servicio */}
                            <div>
                                <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                    Tipo de Servicio
                                </h3>
                                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                                    Selecciona si eres conductor BEI o Conductor/Perceptor (TUVISA normal)
                                </p>
                                <div className="flex gap-3">
                                    {[{ id: 'bei', label: '🚌 BEI', desc: 'Línea BEI (eléctrico)' },
                                      { id: 'servicio', label: '🚍 Servicio', desc: 'TUVISA Normal' }].map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => {
                                                setSelectedTipo(option.id);
                                                // Reset group selections when changing tipo
                                                setSelectedVacaciones('');
                                                setSelectedLibranzas('');
                                            }}
                                            className={`
                                                flex-1 p-4 rounded-xl font-semibold transition-all border
                                                ${selectedTipo === option.id
                                                    ? 'border-cyan-500 bg-cyan-500/20'
                                                    : 'border-[var(--border-color)] hover:bg-[var(--bg-card-hover)]'
                                                }
                                            `}
                                            style={{
                                                color: selectedTipo === option.id ? '#22d3ee' : 'var(--text-primary)'
                                            }}
                                        >
                                            <div className="text-lg">{option.label}</div>
                                            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                                {option.desc}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Vacation Group */}
                            <div>
                                <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                    Grupo de Vacaciones
                                </h3>
                                <select
                                    value={selectedVacaciones}
                                    onChange={(e) => setSelectedVacaciones(e.target.value)}
                                    className="w-full p-3 rounded-xl border appearance-none"
                                    style={{
                                        background: 'var(--bg-tertiary)',
                                        borderColor: 'var(--border-color)',
                                        color: 'var(--text-primary)'
                                    }}
                                >
                                    <option value="">-- Sin asignar --</option>
                                    {gruposVacaciones.map(g => (
                                        <option key={g.id} value={g.id}>
                                            Grupo {g.numero} - {g.periodo_vacaciones || g.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Libranza Group */}
                            <div>
                                <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                                    Grupo de Libranzas
                                </h3>
                                <select
                                    value={selectedLibranzas}
                                    onChange={(e) => setSelectedLibranzas(e.target.value)}
                                    className="w-full p-3 rounded-xl border appearance-none"
                                    style={{
                                        background: 'var(--bg-tertiary)',
                                        borderColor: 'var(--border-color)',
                                        color: 'var(--text-primary)'
                                    }}
                                >
                                    <option value="">-- Sin asignar --</option>
                                    {gruposLibranzas.map(g => (
                                        <option key={g.id} value={g.id}>
                                            {g.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Save Groups Button */}
                            <button
                                onClick={handleSaveGroups}
                                disabled={saving}
                                className="w-full p-4 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-50"
                            >
                                {saving ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                        </div>
                    </SettingsGroup>

                    {/* Anexo Libranzas */}
                    <SettingsGroup title="📋 Anexo de Libranzas">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                    Días libres individuales adicionales.
                                </p>
                                <button
                                    onClick={() => setShowCalendar(!showCalendar)}
                                    className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
                                    title={showCalendar ? "Ocultar calendario" : "Añadir días"}
                                >
                                    {showCalendar ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                                </button>
                            </div>

                            {/* Show selected days */}
                            {anexoDias.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {anexoDias.slice(0, 10).map(d => (
                                        <span
                                            key={d.id}
                                            className="px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-400 flex items-center gap-1"
                                        >
                                            {new Date(d.fecha + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                            <button
                                                onClick={() => handleToggleAnexoDay(d.fecha)}
                                                className="ml-1 hover:text-red-400"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    {anexoDias.length > 10 && (
                                        <span className="px-3 py-1 rounded-full text-sm" style={{ color: 'var(--text-muted)' }}>
                                            +{anexoDias.length - 10} más
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Mini Calendar */}
                            {showCalendar && (
                                <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                                    {/* Month Navigation */}
                                    <div className="flex items-center justify-between mb-3">
                                        <button
                                            onClick={() => setCalendarMonth(m => m > 0 ? m - 1 : 11)}
                                            className="p-2 rounded-lg hover:bg-[var(--bg-card)]"
                                        >
                                            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                                        </button>
                                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                            {monthNames[calendarMonth]} {calendarYear}
                                        </span>
                                        <button
                                            onClick={() => setCalendarMonth(m => m < 11 ? m + 1 : 0)}
                                            className="p-2 rounded-lg hover:bg-[var(--bg-card)]"
                                        >
                                            <ChevronRight className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                                        </button>
                                    </div>

                                    {/* Weekday Headers */}
                                    <div className="grid grid-cols-7 gap-1 mb-1">
                                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
                                            <div key={d} className="text-center text-xs font-medium py-1" style={{ color: 'var(--text-muted)' }}>
                                                {d}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Days */}
                                    <div className="grid grid-cols-7 gap-1">
                                        {generateCalendarDays().map((item) => (
                                            item.empty ? (
                                                <div key={item.key} />
                                            ) : (
                                                <button
                                                    key={item.key}
                                                    onClick={() => handleToggleAnexoDay(item.dateStr)}
                                                    className={`
                                                        p-2 text-center rounded-lg text-sm transition-colors
                                                        ${item.isSelected
                                                            ? 'bg-green-500 text-white'
                                                            : 'hover:bg-[var(--bg-card)]'
                                                        }
                                                    `}
                                                    style={{
                                                        color: item.isSelected ? undefined : 'var(--text-primary)'
                                                    }}
                                                >
                                                    {item.day}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </SettingsGroup>
                </>
            )}

            {/* Loading state for conductor settings */}
            {loading && (
                <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>Cargando...</p>
                </div>
            )}

            {/* User Info Section */}
            <SettingsGroup title="👤 Cuenta">
                <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'var(--bg-tertiary)' }}>
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                {user?.nombre || 'Usuario'}
                            </p>
                            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                {user?.email}
                            </p>
                            {user?.role === 'admin' && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded border border-red-500/30">
                                    Administrador
                                </span>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={logout}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Cerrar sesión</span>
                    </button>
                </div>
            </SettingsGroup>

            {/* App Info */}
            <div className="text-center py-4">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    TUVISA Calendar v1.0.0
                </p>
            </div>
        </div>
    );
};

export default Settings;
